import {
  createMfeLoader,
  type LoaderConfig,
  type MfeLoader,
  type MfeRegistryEntry,
  type MfeHandle,
  type MfeProps,
  type LoadPhaseEvent,
} from '@ey-fso-tax/mfe-loader';
import { getTracker } from '@/telemetry/tracker';
import { clearAppRegistry, getAppLoadMeta, APP_TYPE_IFRAME } from '@/apps/app-registry';
import { ensureAppRegistry } from '@/apps/use-app-tiles';
import { useClientStore, getActiveClientId } from '@/client/client-store';
import { isPopupMfe } from './popup-mfe-constants';
import { isAuth0App } from '@/auth/auth-provider';

/**
 * Dynamic MFE loader service for the shell.
 *
 * Mirrors the Angular shell's `MfeLoaderService` pattern: instead of a single
 * static registry, each appId gets a dynamically-built registry entry at
 * runtime using CDN conventions. Loading strategy per appId:
 *
 *   1. Check `appTypeID` from the app-tile registry (populated by `use-app-tiles`).
 *      - appTypeID === 3 (Iframe) -> load via iframe (same as Angular's `resolveIframeOverride`)
 *   2. If the catalog marks the app as `module-federation` -> try Module Federation first,
 *      with `fallback: 'legacy'` so the mfe-loader cascades to legacy manifest automatically.
 *   3. Otherwise -> load via legacy manifest (`${cdn}/${appId}/manifest.json`).
 *
 * This replaces the old static singleton. Each appId gets its own cached loader
 * instance (matching Angular's `loaderCache` in `mfe-loader.service.ts`).
 */

export interface MfeAppCatalogEntry {
  appId: string;
  label: string;
  description: string;
  category: 'core' | 'delivery' | 'data' | 'admin' | 'dev';
  loader: 'legacy' | 'module-federation';
}

/**
 * Known MFE appIds from `raptor-shell-ui`. Used for metadata (labels, categories,
 * `isStaticMfe` checks) and to determine the preferred loader strategy per app.
 * The actual registry entries are built dynamically at load time.
 */
const MFE_APP_CATALOG = [
  {
    appId: 'home',
    label: 'Home',
    description: 'Portal landing dashboard with upcoming work and deliverable status.',
    category: 'core',
    loader: 'legacy',
  },
  {
    appId: 'workitemuilist',
    label: 'Work Items',
    description: 'Operational work item queue and task list.',
    category: 'delivery',
    loader: 'legacy',
  },
  {
    appId: 'doccenter',
    label: 'Document Center',
    description: 'Client documents, packages, and shared deliverable files.',
    category: 'delivery',
    loader: 'legacy',
  },
  {
    appId: 'dashboards',
    label: 'Dashboards',
    description: 'Reporting and dashboard views for client engagement health.',
    category: 'data',
    loader: 'legacy',
  },
  {
    appId: 'pie',
    label: 'Data',
    description: 'Data tools and PIE workflows.',
    category: 'data',
    loader: 'legacy',
  },
  {
    appId: 'workspace',
    label: 'Apps',
    description: 'Legacy application workspace and app launch experience.',
    category: 'core',
    loader: 'legacy',
  },
  {
    appId: 'admin',
    label: 'Admin',
    description: 'Administration tools and shell configuration.',
    category: 'admin',
    loader: 'legacy',
  },
  {
    appId: 'notification-preferences',
    label: 'Notification Preferences',
    description: 'User notification settings and preferences.',
    category: 'core',
    loader: 'legacy',
  },
  {
    appId: 'myteams',
    label: 'My Teams',
    description: 'Development module federation pilot for team management.',
    category: 'dev',
    loader: 'module-federation',
  },
] as const satisfies readonly MfeAppCatalogEntry[];

export function getMfeAppCatalog(): readonly MfeAppCatalogEntry[] {
  return MFE_APP_CATALOG;
}

/**
 * True when `appId` is in the static catalog (known apps + orchestratoruitasks).
 * Used for metadata lookups; loading strategy no longer depends on this.
 */
export function isStaticMfe(appId: string): boolean {
  const id = appId.toLowerCase();
  return MFE_APP_CATALOG.some((a) => a.appId === id) || id === 'orchestratoruitasks';
}

// ─── URL Helpers ───────────────────────────────────────────────────────────────

function getLegacyBaseUrl(): string {
  const legacyBaseUrlRaw = import.meta.env.VITE_MFE_LEGACY_BASE_URL ?? '/static';
  const legacyBaseUrlNoTrail = legacyBaseUrlRaw.replace(/\/+$/, '');
  return /^https?:\/\//i.test(legacyBaseUrlNoTrail)
    ? legacyBaseUrlNoTrail
    : `${window.location.origin}${
        legacyBaseUrlNoTrail.startsWith('/') ? '' : '/'
      }${legacyBaseUrlNoTrail}`;
}

// appId shape gate: lowercase slug (letter-led, [a-z0-9_-], 2-63 chars).
const VALID_APP_ID_RE = /^[a-z][a-z0-9_-]{1,62}$/;

// ─── Dynamic Entry Builders ────────────────────────────────────────────────────

/**
 * Build a Module Federation entry with legacy fallback for an appId.
 * Convention (mirrors Angular `mfe-loader.service.ts` `buildEntryForApp`):
 *   - remoteEntry: `${cdn}/${appId}/remoteEntry.js`
 *   - remoteName: appId
 *   - exposedModule: ./App
 *   - fallback: 'legacy' (cascades to manifest.json if remoteEntry fails)
 */
function buildModuleFederationEntry(appId: string, baseUrl: string): MfeRegistryEntry {
  const normalizedId = appId.toLowerCase();
  return {
    appId,
    loader: 'module-federation',
    remoteEntry: `${baseUrl}/${normalizedId}/remoteEntry.js`,
    remoteName: normalizedId,
    exposedModule: './App',
    scriptType: 'classic',
    fallback: 'legacy',
    legacyBaseUrl: baseUrl,
    legacySelector: `${normalizedId}-component`,
  };
}

/**
 * Build a Module Federation v2 manifest entry for Modern.js/Rspack remotes.
 * Modern.js emits both `mf-manifest.json` and `remoteEntry.js`; the manifest
 * path is preferred because it carries hashed chunks/CSS metadata.
 */
function buildModuleFederationV2Entry(appId: string, baseUrl: string): MfeRegistryEntry {
  const normalizedId = appId.toLowerCase();
  return {
    appId,
    loader: 'module-federation-v2',
    manifestUrl: `${baseUrl}/${normalizedId}/mf-manifest.json`,
    remoteEntry: `${baseUrl}/${normalizedId}/remoteEntry.js`,
    remoteName: normalizedId,
    exposedModule: './App',
    runtimeShared: 'isolated',
    fallback: 'legacy',
    legacyBaseUrl: baseUrl,
    legacySelector: `${normalizedId}-component`,
  };
}

/**
 * Build an iframe registry entry. Used for apps with `appTypeID === 3` or
 * standalone per-client apps whose webpack `publicPath` requires their own
 * document (chunks resolve relative to the iframe's base, not the shell route).
 */
function buildIframeEntry(appId: string, iframeUrl?: string): MfeRegistryEntry {
  const clientId = getActiveClientId();
  const base = clientId
    ? `${window.location.origin}/${clientId}/app-container/${appId}`
    : `${getLegacyBaseUrl()}/${appId}`;
  return {
    appId,
    loader: 'iframe',
    iframeUrl: iframeUrl || `${base}/index.html?nomock`,
    iframeTitle: appId,
    iframeAllow: 'clipboard-read; clipboard-write; fullscreen',
    iframeSandbox: '',
    postMessageInit: false,
  };
}

/**
 * Dynamically resolve the appropriate registry entry for any appId.
 *
 * Simple two-branch strategy:
 *   1. appTypeID === 3 (IframeApp) OR popup MFE → iframe.
 *   2. Everything else → Module Federation first, with automatic fallback
 *      to legacy manifest.json (`fallback: 'legacy'`).
 */
function resolveEntryForApp(appId: string, baseUrl: string): MfeRegistryEntry {
  const normalizedId = appId.toLowerCase();

  if (!VALID_APP_ID_RE.test(normalizedId)) {
    throw new Error(`[MfeLoader] Invalid appId: "${appId}"`);
  }

  // Popup MFEs load via module-federation (same as Angular).
  // No special handling, they fall through to the default MF+legacy path below.

  // Explicit iframe app from the backend tile feed (appTypeID === 3).
  // Popups are excluded; Angular never uses iframe for popups.
  // Auth0-enabled MFEs are also excluded (PRTL-2857): they are Module-Federation
  // apps that acquire their own Auth0 token in-context, so we skip the iframe
  // override for them — mirroring the Angular POC `mfe-container.component.ts`,
  // which bypasses `resolveIframeOverride` when the provider is Auth0.
  const meta = getAppLoadMeta(normalizedId);
  if (
    meta &&
    meta.appTypeID === APP_TYPE_IFRAME &&
    !isPopupMfe(normalizedId) &&
    !isAuth0App(normalizedId)
  ) {
    const entry = buildIframeEntry(normalizedId, meta.externalRoutePath);
    if (import.meta.env.DEV) {
      console.warn(
        `[MfeLoader] "${appId}" -> iframe (appTypeID=3):`,
        (entry as { iframeUrl?: string }).iframeUrl,
      );
    }
    return entry;
  }

  if (isModernModuleFederationApp(normalizedId)) {
    if (import.meta.env.DEV) {
      console.warn(`[MfeLoader] "${appId}" -> module-federation-v2 (Modern.js)`);
    }
    return buildModuleFederationV2Entry(normalizedId, baseUrl);
  }

  // Default: try Module Federation first, fall back to legacy manifest.
  if (import.meta.env.DEV) {
    console.warn(`[MfeLoader] "${appId}" -> module-federation (fallback: legacy)`);
  }
  return buildModuleFederationEntry(normalizedId, baseUrl);
}

// ─── Loader Service (mirrors Angular MfeLoaderService) ─────────────────────────

const loaderCache = new Map<string, MfeLoader>();

const localOverrides = parseLocalOverrides();
// Apps whose remotes are Modern.js/Rspack MF v2 (served via `mf-manifest.json`).
// Empty by default and opt-in per environment via `VITE_MODERN_MFE_APP_IDS`: an
// app forced here without a deployed `mf-manifest.json` fails to load (404 →
// legacy fallback). `usir` migrated to Modern.js MF v2 and is enabled via
// `VITE_MODERN_MFE_APP_IDS` per environment (set on DEV; other envs opt in once
// their v2 remote is deployed).
const MODERN_MODULE_FEDERATION_APPS = parseAppIdSet(import.meta.env.VITE_MODERN_MFE_APP_IDS, []);

function isModernModuleFederationApp(appId: string): boolean {
  return MODERN_MODULE_FEDERATION_APPS.has(appId);
}

function parseAppIdSet(raw: string | undefined, fallback: readonly string[]): ReadonlySet<string> {
  const ids = (raw ?? fallback.join(','))
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s) => VALID_APP_ID_RE.test(s));
  return new Set(ids);
}

function parseLocalOverrides(): Readonly<Record<string, string>> {
  if (!import.meta.env.DEV) return {};

  const raw = import.meta.env.VITE_MFE_LOCAL_OVERRIDES ?? '';
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .reduce<Record<string, string>>((acc, part) => {
      const [appId, url] = part.split('=').map((s) => s.trim());
      if (!appId || !url || !VALID_APP_ID_RE.test(appId.toLowerCase())) {
        return acc;
      }
      try {
        acc[appId.toLowerCase()] = new URL(url, window.location.origin).href.replace(/\/+$/, '');
      } catch {
        // Invalid local override, ignore it and let the normal CDN base resolve.
      }
      return acc;
    }, {});
}

function buildBaseConfig(): Omit<LoaderConfig, 'registry'> {
  return {
    // Origin allow-listing is enforced at the gateway/infrastructure layer, so
    // the loader's client-side origin check is intentionally disabled. An empty
    // list is treated as "allow all" by @ey-fso-tax/mfe-loader (isAllowedUrl).
    allowedOrigins: [],
    originPolicy: import.meta.env.PROD ? 'strict' : 'warn',
    validation: 'strict',
    readiness: {
      eventName: 'childAppLoadResponse',
      enabled: false,
    },
    emitLifecycleEvents: true,
    sloBudget: {
      totalMs: 5000,
      remoteEntryMs: 3000,
      mountMs: 1000,
    },
    onError: (appId, err) => {
      // Suppress remote-entry failures in dev — expected for legacy-only apps
      // that don't ship a remoteEntry.js (the fallback handles it).
      if (err.phase === 'remote-entry') return;

      const cause = err.cause;
      const causeMessage =
        cause instanceof Error ? cause.message : cause !== undefined ? String(cause) : '';
      const message = causeMessage ? `${err.message}: ${causeMessage}` : err.message;
      const reported = new Error(message);
      if (cause instanceof Error && cause.stack) reported.stack = cause.stack;

      getTracker().error(reported, {
        appId,
        phase: err.phase,
        loaderType: err.loaderType,
        causeName: cause instanceof Error ? cause.name : undefined,
      });
    },
    onPhase: (event: LoadPhaseEvent) => {
      if (event.phase === 'load') {
        if (event.success) {
          console.warn(
            `[MfeLoader] "${event.appId}" loaded (${event.loaderType}, ${event.durationMs}ms)`,
          );
        } else {
          console.error(
            `[MfeLoader] "${event.appId}" failed to load (${event.loaderType}, ${event.durationMs}ms)`,
            event.error,
          );
        }
      }
      // Only track failures — success events fire on every MFE mount (high volume).
      if (!event.success) {
        getTracker().event(`mfe-${event.phase}`, {
          appId: event.appId,
          durationMs: event.durationMs,
          loaderType: event.loaderType,
          success: event.success,
        });
      }
    },
  };
}

/**
 * Get or create a loader instance for a given appId. Each app gets its own
 * loader with a dynamically-built registry entry (mirrors Angular's per-app
 * `loaderCache` in `mfe-loader.service.ts`).
 */
async function getLoaderForApp(appId: string): Promise<MfeLoader> {
  const normalizedId = appId.toLowerCase();
  if (loaderCache.has(normalizedId)) {
    return loaderCache.get(normalizedId)!;
  }

  // Ensure the app load-registry (appTypeID / externalRoutePath) is populated
  // for the active client BEFORE resolving the entry. The registry is otherwise
  // vulnerable to a race: `beforeLoad` warms it, but the cold-start client-store
  // hydration (null → clientId) fires `clearAppRegistry()` (see the client
  // subscription below) right before the MFE mounts — leaving it empty, so an
  // iframe app (appTypeID 3) would misresolve to module-federation and 404.
  // Re-applies from the persistent per-client cache when available (no refetch).
  const activeClientId = getActiveClientId();
  if (activeClientId) {
    await ensureAppRegistry(activeClientId);
  }

  const baseUrl = localOverrides[normalizedId] || getLegacyBaseUrl();

  const entry = resolveEntryForApp(normalizedId, baseUrl);

  const loader = createMfeLoader({
    ...buildBaseConfig(),
    registry: { type: 'static', entries: [entry] },
  });
  await loader.initialize();
  loaderCache.set(normalizedId, loader);
  return loader;
}

// ─── Public API ────────────────────────────────────────────────────────────────

/**
 * Load an MFE dynamically by appId into a container element.
 * Resolves the correct loading strategy (iframe / module-federation / legacy)
 * based on the app-tile registry and catalog metadata.
 */
export async function loadMfe(
  appId: string,
  container: HTMLElement,
  props?: MfeProps,
): Promise<MfeHandle> {
  const normalizedId = appId.toLowerCase();
  if (!isPopupMfe(normalizedId)) {
    document.title = 'Global Tax Platform';
  }
  const loader = await getLoaderForApp(normalizedId);
  return loader.load(normalizedId, container, props);
}

/**
 * Unload a previously-loaded MFE from its container.
 */
export async function unloadMfe(appId: string, container: HTMLElement): Promise<void> {
  const normalizedId = appId.toLowerCase();
  const loader = loaderCache.get(normalizedId);
  if (loader) {
    return loader.unload(normalizedId, container);
  }
}

/**
 * Preload specific apps by creating their loader instances (fetches
 * manifests / remoteEntry without mounting).
 */
export async function preloadMfe(appIds: string[]): Promise<void> {
  await Promise.all(appIds.map((id) => getLoaderForApp(id)));
}

/**
 * Invalidate a cached loader (e.g. on client switch, when appTypeID might
 * change for the same appId). Next `loadMfe` will rebuild the entry.
 */
export function invalidateLoaderCache(appId?: string): void {
  if (appId) {
    loaderCache.delete(appId.toLowerCase());
  } else {
    loaderCache.clear();
  }
}

// On client change, clear all cached loaders so iframe entries (which bake in
// the clientId URL) are rebuilt with the new client. Mirrors Angular's
// `reloadMfeOnClientChange` which re-runs `loadMFEApp()` on client switch.
useClientStore.subscribe((state, prev) => {
  const id = state.current?.id ?? null;
  const prevId = prev.current?.id ?? null;
  if (id !== prevId && id != null) {
    invalidateLoaderCache();
    clearAppRegistry();
  }
});
